import { cn } from '@up2stay/up2promo-ui';

interface OnboardingProgressBarProps {
  currentStep: number;
  totalSteps: number;
  /** Optional named labels, 1:1 with steps (1-indexed currentStep). */
  stepLabels?: string[];
  className?: string;
}

export function OnboardingProgressBar({
  currentStep,
  totalSteps,
  stepLabels,
  className,
}: OnboardingProgressBarProps) {
  const progress = Math.min(100, Math.max(0, (currentStep / totalSteps) * 100));
  const hasLabels = Boolean(stepLabels?.length);

  return (
    <div className={cn('w-full', className)}>
      <div
        className="h-1 w-full bg-muted"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        aria-valuenow={currentStep}
        aria-label={
          stepLabels?.[currentStep - 1]
            ? undefined
            : `Step ${currentStep} of ${totalSteps}`
        }
      >
        <div
          className="h-full rounded-r-full bg-primary transition-[width] duration-300 ease-out motion-reduce:transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      {hasLabels ? (
        <ol className="flex items-center justify-between gap-2 px-4 pt-2 md:px-8">
          {stepLabels!.map((label, index) => {
            const step = index + 1;
            const isCurrent = step === currentStep;
            const isDone = step < currentStep;
            return (
              <li
                key={label}
                className={cn(
                  'min-w-0 flex-1 text-center text-[11px] font-semibold uppercase tracking-[0.6px]',
                  isCurrent && 'text-[color:var(--ghost-accent-fg)]',
                  isDone && 'text-muted-foreground',
                  !isCurrent && !isDone && 'text-muted-foreground/70',
                )}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span className="truncate">{label}</span>
              </li>
            );
          })}
        </ol>
      ) : null}
    </div>
  );
}
